import React, { useState, useEffect, useContext } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useParams } from "react-router";
import MainHeader from "../../Components/Header/MainHeader";
import MainHeaderRedemption from "../../Components/Header/MainHeaderRedemption";
import EachRedemptionProduct from "../../Components/Shopping/EachRedemptionProduct";
import { BASE_URL_API } from "../../References/URLs";
import AuthContext from "../../Store/AuthContext";
import styles from "./ProductsListingRedem.module.css";

const ProductsListingRedem = () => {
    const { storeKey } = useParams();
    const subdomain = "shop";
    const [cartItems, setCartItems] = useState([]);
    const authCtx = useContext(AuthContext);
    const isLoggedIn = authCtx?.isLoggedIn;
    const loginData = authCtx?.loginData ? JSON.parse(authCtx.loginData) : null;
    const [categorySubcategoryData, setCategorySubcategoryData] = useState([]);
    const [hasMore, setHasMore] = useState(true); // Initially, assume there are more products
    const [products, setProducts] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState({});
    const [selectedSubcategories, setSelectedSubcategories] = useState({});
    const [page, setPage] = useState(1); // Start from page 1
    const [loading, setLoading] = useState(false);
    const [isPointsBased, setIsPointsBased] = useState("0")

    // Get categories and subcategories data
    useEffect(() => {
        getCatalogueCategories();
    }, []);

    const getCatalogueCategories = async () => {
        const response = await fetch(BASE_URL_API + "getCatalogueCategories", {
            method: "POST",
            body: JSON.stringify({ subdomain }),
        });

        if (!response.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const respo = await response.json();
            if (respo.status === "success") {
                setCategorySubcategoryData(respo?.response);
            } else if (respo.message === "token Expired, please login again!") {
                authCtx.logout();
            }
        }
    };

    // Filter change handler
    const selectSubcategoryHandler = (categoryIdentifier, subCategoryIdentifier) => {
        setSelectedSubcategories((prev) => {
            const categorySubs = new Set(prev[categoryIdentifier] || []);
            if (categorySubs.has(subCategoryIdentifier)) {
                categorySubs.delete(subCategoryIdentifier);
            } else {
                categorySubs.add(subCategoryIdentifier);
            }

            const updatedSubcategories = { ...prev, [categoryIdentifier]: categorySubs };
            const allSubCategories = categorySubcategoryData
                .find((cat) => cat.categoryIdentifier === categoryIdentifier)?.subCategory?.map((sub) => sub.subCategoryIdentifier) || [];

            setSelectedCategories((prev) => ({
                ...prev,
                [categoryIdentifier]: categorySubs.size === allSubCategories.length,
            }));

            return updatedSubcategories;
        });
    };

    const selectCategoryHandler = (categoryIdentifier) => {
        setSelectedCategories((prev) => {
            const isSelected = !prev[categoryIdentifier];

            setSelectedSubcategories((prevSubs) => {
                const updatedSubcategories = { ...prevSubs };
                const allSubCategories = categorySubcategoryData.find((cat) => cat.categoryIdentifier === categoryIdentifier)?.subCategory || [];

                if (isSelected) {
                    updatedSubcategories[categoryIdentifier] = new Set(allSubCategories.map((sub) => sub.subCategoryIdentifier));
                } else {
                    updatedSubcategories[categoryIdentifier] = new Set();
                }

                return updatedSubcategories;
            });

            return { ...prev, [categoryIdentifier]: isSelected };
        });
    };

    // Reset page and loading state when filters change
    useEffect(() => {
        setProducts([]);
        setPage(1); // Reset to page 1
        setHasMore(true); // Set hasMore to true again
        setLoading(false); // Reset loading state
        window.scrollTo(0, 0); // Scroll to top
    }, [selectedCategories, selectedSubcategories]); // Triggers when filter changes

    // Fetch products when page or filters change
    useEffect(() => {
        const getCatalogueProducts = async () => {
            if (loading || !hasMore) return; // Prevent fetching if already loading or no more data

            setLoading(true);

            const selectedCategoriesList = Object.entries(selectedCategories)
                .filter(([categoryIdentifier, isSelected]) => isSelected)
                .map(([categoryIdentifier, _]) => categoryIdentifier);

            const selectedSubcategoriesList = Object.entries(selectedSubcategories)
                .flatMap(([categoryIdentifier, subCategorySet]) => Array.from(subCategorySet));

            const requestBody = {
                subdomain,
                page,
                categoriesData: selectedCategoriesList,
                subcategoriesData: selectedSubcategoriesList,
            };

            const response = await fetch(BASE_URL_API + "getCatalogueProducts", {
                method: "POST",
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                console.log("Server Error");
            } else {
                const respo = await response.json();
                if (respo.status === "success") {
                    setProducts((prev) => {
                        const existingIds = new Set(prev.map((p) => p.productIdentifier));
                        const filteredNewProducts = respo.response.filter((p) => !existingIds.has(p.productIdentifier));
                        return [...prev, ...filteredNewProducts];
                    });

                    setPage((prev) => prev + 1); // Increment the page number
                    setIsPointsBased(respo.isPointsBased); // Increment the page number

                    if (!respo.hasMore) {
                        setHasMore(false); // No more products
                    }
                }
            }

            setLoading(false);
        };

        getCatalogueProducts();
    }, [page, selectedCategories, selectedSubcategories]); // Trigger when page or filters change

    // Scroll event for infinite scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop >=
                document.documentElement.offsetHeight - 100
            ) {
                if (!loading && hasMore) {
                    setPage((prev) => prev + 1); // Trigger next page fetch
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [loading, hasMore]);

    const getCartItems = async () => {
        const getCartItemsResponse = await fetch(BASE_URL_API + "getCartItemsRedemption", {
            method: "POST",
            body: JSON.stringify({
                token: authCtx.token,
                userIdentifier: loginData.userIdentifier,
            }),
        });

        if (getCartItemsResponse.ok) {
            const response = await getCartItemsResponse.json();
            if (response.status === "success") {
                setCartItems(response?.response);
            } else if (response.message === "token Expired, please login again!") {
                authCtx.logout();
            } else {
                setCartItems([]);
            }
            console.log(response)
        } else {
            console.error("Server Error");
        }
    };

    useEffect(() => {
        if (isLoggedIn) {
            getCartItems();
        }
    }, [isLoggedIn]);

    return (
        <div>
            <MainHeaderRedemption cartItems={cartItems} />
            <div className={styles.container}>
                <div className={styles.contentWrapper}>
                    <aside className={`${styles.filters} ${styles.animated}`} style={{ position: "sticky", top: "0", height: "91svh", overflowY: "auto" }}>
                        <div className={styles.filterTitle}>Filters</div>
                        <div className={styles.filterSection}>
                            <div className={styles.sectionTitle}>Categories</div>
                            {categorySubcategoryData?.length > 0 ? (
                                <div>
                                    {categorySubcategoryData?.map((categoryItem) => (
                                        <div key={categoryItem.categoryIdentifier}>
                                            <div className={styles.categoryTitle} onClick={() => selectCategoryHandler(categoryItem.categoryIdentifier)}>
                                                <div>
                                                    {selectedCategories[categoryItem.categoryIdentifier] ? <MdCheckBox size={18} /> : <MdCheckBoxOutlineBlank size={18} />}
                                                </div>
                                                <div className={styles.category}>{categoryItem.category}</div>
                                            </div>
                                            <div className={styles.subCategoryWrapper}>
                                                {categoryItem?.subCategory?.map((subCategoryItem) => (
                                                    subCategoryItem?.subCategoryIdentifier !== "" && (
                                                        <div
                                                            key={subCategoryItem.subCategoryIdentifier}
                                                            className={styles.eachSubCategory}
                                                            onClick={() => selectSubcategoryHandler(categoryItem.categoryIdentifier, subCategoryItem.subCategoryIdentifier)}
                                                        >
                                                            <div className={styles.iconHolder}>
                                                                {selectedSubcategories[categoryItem.categoryIdentifier]?.has(subCategoryItem.subCategoryIdentifier) ? (
                                                                    <MdCheckBox size={18} />
                                                                ) : (
                                                                    <MdCheckBoxOutlineBlank size={18} />
                                                                )}
                                                            </div>
                                                            <div>{subCategoryItem.subCategoryLabel}</div>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div>No data found!</div>
                            )}
                        </div>
                    </aside>

                    <div className={styles.gridWrapper}>
                        <div className={styles.productsHeader}>
                            <div>Products</div>
                        </div>
                        <div className={styles.grid} style={{ paddingBottom: "80px" }}>
                            {products.map((product) => (
                                <EachRedemptionProduct
                                    key={product.productIdentifier}
                                    product={product}
                                    cartItems={cartItems}
                                    getCartItems={getCartItems}
                                    isPointsBased={isPointsBased}
                                />
                            ))}
                        </div>
                        {loading && <div className={styles.loading}>Loading more products...</div>}
                        {!loading && !hasMore && <div className={styles.loading}>That's all</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductsListingRedem;
